import { useMutation, useQuery } from '@apollo/client';
import { isNil } from 'lodash';
import React from 'react';
import styled from 'styled-components';
import { Spinner, SpinnerSize } from '@fluentui/react';
import { globalFormat } from '../../utils/formatting';
import usePermission from '../../utils/hooks/permissionHooks/usePermission';
import {
  DataRefresh,
  DataRefreshState,
  IGetFieldDataRefreshQuery,
  IGetFieldDataRefreshQueryVariables,
  IRefreshFieldDataMutation,
  IRefreshFieldDataMutationVariables,
  PermissionKey,
} from '../../utils/apollo/graphqlSchema';
import { REFRESH_FIELD_DATA } from '../../utils/apollo/dataRefresh/mutations';
import toastMessage from '../../utils/toastMessage';
import PermissionElementWrapper from '../../pages/PermissionControls/PermissionElementWrapper';
import KubitTooltip from '../KubitTooltip';
import { IconWrapper, SvgIcon } from '../Icon';
import { IFieldValueDataRefreshProps } from './types';
import { UnreachableCaseError } from 'ts-essentials';
import { GET_FIELD_REFRESH_DATA } from '../../utils/apollo/dataRefresh/queries';
import { assertNotNil } from '../../utils/types';
import useOnDataRefreshEndEffect from './useOnDataRefreshEndEffect';
import { theme } from '../../styles/theme';

const Container = styled.div`
  display: flex;
  font-size: 10px;
  font-weight: 500;
  line-height: normal;
  color: ${theme.colors.secondary800};
  min-height: 28px;
  height: 28px;
  padding: 0 4px 0 8px;
  align-items: center;
  gap: 8px;
  border-top: 1px solid ${theme.colors.border};
`;

const Label = styled.div`
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${theme.colors.secondary600};
`;

const pollIntervalMs = 30 * 1000; // every 30 seconds

const FieldValueDataRefresh = ({
  schemaId,
  field,
  onRefreshEnd,
}: IFieldValueDataRefreshProps) => {
  const { dateFormat, timeFormat } = globalFormat.getGlobalFormat();

  const { checkIsPermitted } = usePermission();

  const isPermittedToRefreshData = checkIsPermitted({
    requiredPermissionKeys: [PermissionKey.DataRefresh],
    schemaId,
  });

  const {
    loading,
    data,
    refetch: refetchGetFieldRefreshData,
  } = useQuery<IGetFieldDataRefreshQuery, IGetFieldDataRefreshQueryVariables>(
    GET_FIELD_REFRESH_DATA,
    {
      variables: { schemaId, fieldKey: field.key },
      pollInterval: pollIntervalMs,
    },
  );

  const [refreshData] = useMutation<
    IRefreshFieldDataMutation,
    IRefreshFieldDataMutationVariables
  >(REFRESH_FIELD_DATA, {
    variables: { input: { schemaId, fieldKey: field.key } },
  });

  useOnDataRefreshEndEffect(data?.fieldRefreshMeta.refreshState, () => {
    toastMessage({
      type: 'info',
      message: `Refresh complete! "${field.name}" values are up to date.`,
    });

    onRefreshEnd?.();
  });

  const handleRefreshData = async () => {
    const { data, errors } = await refreshData();
    if (
      !errors &&
      data?.fieldRefresh.entity &&
      data?.fieldRefresh.errors.length === 0
    ) {
      const state = data.fieldRefresh.entity;
      if (state === DataRefresh.NotSupported) {
        toastMessage({
          type: 'error',
          message: `Data refresh is not supported for "${field.name}"`,
        });
      } else if (state === DataRefresh.Skipped) {
        toastMessage({
          type: 'info',
          message: `The field "${field.name}" was recently refreshed and is not due to refresh yet.`,
        });
      } else if (state === DataRefresh.Started) {
        toastMessage({
          type: 'info',
          message: `The refresh is in progress and will be completed shortly.`,
        });
      } else {
        throw new UnreachableCaseError(state);
      }
    } else {
      toastMessage({
        message: `Failed to refresh "${field.name}" field`,
        type: 'error',
      });
    }

    await refetchGetFieldRefreshData();
  };

  let content;
  if (loading) {
    content = (
      <>
        <Label>Loading &quot;{field.name}&quot; refresh state</Label>
        <Spinner size={SpinnerSize.xSmall} />
      </>
    );
  } else {
    assertNotNil(data);
    const { refreshState, latestRefreshAt } = data.fieldRefreshMeta;
    const isRunning = refreshState === DataRefreshState.Running;
    if (isRunning) {
      content = (
        <>
          <Label>Refreshing &quot;{field.name}&quot;...</Label>
          <Spinner size={SpinnerSize.xSmall} />
        </>
      );
    } else {
      content = (
        <>
          <Label>
            Last refreshed at{' '}
            {!isNil(latestRefreshAt)
              ? globalFormat.formatDate(latestRefreshAt, {
                  dateFormat,
                  timeFormat,
                })
              : 'N/A'}
          </Label>
          <div>
            <PermissionElementWrapper isPermitted={isPermittedToRefreshData}>
              <KubitTooltip
                content={`Refresh the values of the "${field.name}" field`}
                placement="right"
                disabled={!isPermittedToRefreshData}
                noOpenDelay={true}
              >
                <IconWrapper
                  size={16}
                  onClick={
                    isPermittedToRefreshData ? handleRefreshData : undefined
                  }
                >
                  <SvgIcon
                    name="rolling-icon"
                    customFill="currentColor"
                    isDisabled={!isPermittedToRefreshData}
                  />
                </IconWrapper>
              </KubitTooltip>
            </PermissionElementWrapper>
          </div>
        </>
      );
    }
  }
  return <Container>{content}</Container>;
};

export default FieldValueDataRefresh;
